<script setup lang="ts">
definePageMeta({
  layout: "no-bread-crumbs",
});

useHead({
  title: "新卒採用",
  meta: [{ name: "description", content: "ファミリー庭園新卒採用フォーム" }],
});
const { recruitData, resetData } = useRecruit();
const { isChange } = useLeaveForms();

if (recruitData.value.form_type != "newgrad") {
  // formtypeが異なる場合リセットをかける
  resetData();
  // formtypeを現在のformtypeに設定
  recruitData.value.form_type = "newgrad";
}

watchOnce(
  () => recruitData.value,
  () => {
    isChange.value = true;
    recruitData.value.recruit_cd = 1;
  },
  { deep: true },
);

function handleSubmit(e: Event) {
  isChange.value = false;
  useRouter().push("/recruit/confirm");
}

const recruitFlow = [
  "会社説明",
  " 面接(複数回)・適正テスト",
  "内定",
  "入社（4/1)",
];
</script>

<template>
  <RecruitPage title="新卒採用">
    <template #content>
      <div class="p-recruit__content">
        <h2 class="p-recruit__h2">ファミリー庭園とは？</h2>
        <p>
          <span class="block"
            >当社は、家の外まわり（外構・お庭など）をデザイン・設計・施工する会社です。</span
          >
          <span class="block"
            >ホームページの施工実績数やお客様の声などを見ていただければ分かるかと思われますが、弊社は日本一の規模を誇る、業界のリーディングカンパニーです。</span
          >
        </p>
        <p class="text-4xl font-serif italic mt-10 mb-4">Leading Company</p>
        <ImageEl
          class="w-full"
          src="/images/recruit/leading-company.png"
          alt="Leading Company"
          width="1900"
          height="950"
          sizes="sm:100vw lg:1000px"
          format="webp"
        />
      </div>
      <div class="p-recruit__content">
        <h2 class="p-recruit__h2">私たちの仕事</h2>
        <div
          class="text-center tablet:text-left tablet:flex tablet:justify-center tablet:items-center"
        >
          <ImageEl
            class="hidden mr-5 tablet:block tablet:w-3/5"
            src="/images/recruit/shinsotsu1.png"
            alt="Leading Company"
            width="1391"
            height="1432"
            sizes="sm:100% lg:800px"
            format="webp"
          />
          <div class="flex-1">
            <h2 class="p-recruit__h3">
              外構が大切な住まいの
              <br />第一印象を決める。
            </h2>
            <p>
              「イケメンなのにファッションセンスがイマイチ」とか「メイクが時代遅れで損してる人」っていますよね？
              それは住まいでも同じこと。待望のマイホームを建てることになって間取りやリビングのテイスト・
              家族の希望など様々なイメージを施工業者さんに伝えながらどんどん「夢を形に」していきます。
            </p>
            <p class="my-10">
              しかし、意外とイメージしにくいのが「外構」と呼ばれる生け垣や庭・カーポート
              といった建物以外の家を構成する要素です。建物が人の顔とするなら外構はその人が着ている服。
              顔以上にその人の趣味やセンス、時には人柄までも表現するように、外構はその家に住む人のイメージ、
              そして魅力的な外構を持つ家が建ち並ぶ姿はその街のイメージすら形づくると私たちは考えます。
            </p>
            <p>
              私たちファミリー庭園は創業以来、数々の住まいの外構を手がけその豊富な施工実績に基づき、
              お客様の描いたイメージをプロの視点で魅力的な外構を実現することにより、住む人の快適な暮らしのお手伝いをしています。
            </p>
          </div>
        </div>
      </div>
      <div class="p-recruit__content">
        <h2 class="p-recruit__h2">求めている人材</h2>
        <p>求めている人材は「真面目な方」です。</p>

        <p class="mb-10">
          真面目に弊社のノウハウを身につけて頂ければ必ず一人前になることができます。
        </p>
        <p>
          成果があればそれにみあったステージを用意してあなたのやる気に応える会社です。
        </p>
        <p class="mb-10">
          真面目で、自分自身の成長を楽しめる方の応募をお待ちしています！
        </p>
        <ImageEl
          class="mx-auto"
          src="/images/recruit/shinsotsu2.png"
          alt="Leading Company"
          width="1000"
          height="283"
          sizes="sm:100% lg:800px"
          format="webp"
        />
      </div>
      <div class="p-recruit__content">
        <h2 class="p-recruit__h2">会社紹介動画</h2>
        <FacadeYoutube
          title="ファミリー庭園会社紹介動画"
          videoId="i3lkS_qyZzI"
          frameborder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
        ></FacadeYoutube>
      </div>
      <div class="p-recruit__content">
        <h2 class="p-recruit__h2">採用フロー</h2>
        <ListFlowBox :flow="recruitFlow" />
      </div>
      <div
        class="border border-gray-500 mx-4 p-4 tablet:px-28 tablet:mx-auto max-w-2xl"
      >
        <h2 class="text-2xl font-bold text-red-600 mb-5">
          内定者対象 コロナ学費支援有ります
        </h2>
        <p class="font-bold text-lg mb-2 text-left">
          無利子融資：<span class="text-red-600">最大50万円</span><br />
          内10万円は会社支援致します！
        </p>
        <p class="text-left">
          <span class="block">返済方法については相談可。最大2年</span>
          <span class="block">※10万円支援は支援策とセットになります。</span>
          <span class="block"
            >※「みどりの就活」などの就活マッチングサイトを経由しエントリーした方は対象外になります。</span
          >
        </p>
      </div>
      <div class="p-recruit__content">
        <h2 class="p-recruit__h2">会社説明会</h2>
        <p>
          <span class="block"
            >下記のお問い合わせフォームもしくはお電話にて、事前予約をお願いします。</span
          >
          <span class="block">その際に時間などを調整いたします。</span>
          <span class="block"
            >また説明会後、すぐ1次面接させて頂くことも可能ですので、ご希望の方は履歴書をご持参ください。</span
          >
        </p>
        <p class="font-bold text-red-600 mt-5">WEB説明会・面接も随時開催中</p>
      </div>
      <div class="p-recruit__content">
        <div class="p-recruit__content--entry">
          <div class="entry">
            <p>お電話でのご予約はこちらから</p>
            <p class="tel-number">
              <Icon size="24" name="mdi:phone" />
              06-6777-9544
            </p>
            <p>
              ネットでのご予約、お申し込みは下記メールフォームより受け付けております
            </p>
          </div>
        </div>
      </div>
    </template>
    <template #form>
      <FormParent :submit="handleSubmit">
        <RecruitForm type="newgrad"></RecruitForm>
      </FormParent>
    </template>
  </RecruitPage>
</template>
